import { MagnifyImage } from "@/components/MagnifyImage/MagnifyImage";
import {
  BodyText,
  Callout,
  Collapsible,
  Skeleton,
  Tooltip,
} from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { Separator } from "@/components/ui/Separator";
import { useRoomToken } from "@/features/Patient/network/useRoomToken";
import { useAmazonAttachment } from "@/hooks/useAmazonAttachment";
import { formatDateTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { useEncounter } from "@/network/useEncounter";
import { useParams, useSearch } from "@tanstack/react-router";
import parse from "html-react-parser";
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page } from "react-pdf";
import { useResizeDetector } from "react-resize-detector";
import {
  type ExtractedAllergy,
  type ProcessedCondition,
  type ProcessedMedication,
  type VisitIntakeAllergyData,
  useAmazonVisitIntake,
} from "./useAmazonVisitIntake";

import { ThumbsDown } from "lucide-react";
import { CiqAllergy } from "./CiqAllergy";
import { CiqAllergyMismatch } from "./CiqAllergyMismatch";
import { CiqAllergyNoKnown } from "./CiqAllergyNoKnown";
import { CiqCondition } from "./CiqCondition";
import { CiqMedication } from "./CiqMedication";
import { useConditionExtractionFeedback } from "./useConditionExtractionFeedback";
import { useMarkConditionExtractionAsErroneous } from "./useMarkConditionExtractionAsErroneous";
import { useMarkMedicationExtractionAsErroneous } from "./useMarkMedicationExtractionAsErroneous";
import { useMedicationExtractionFeedback } from "./useMedicationExtractionFeedback";

interface CiqItem {
  linkId: string;
  text: string;
  answer?: Array<{
    valueString?: string;
    valueAttachment?: {
      url: string;
    };
  }>;
}

const STATIC_ALLERGEN_CIQ_LINK_ID =
  "amzn1.katara.qms.question.id.generatedciqprovideralerts";

const InlineAttachment = ({
  url,
  className,
  magnifyImageOffsetRef,
}: {
  url: string;
  className?: string;
  magnifyImageOffsetRef?: RefObject<Element | null>;
}) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data: attachmentBlob } = useAmazonAttachment({
    patientId: patientId as string,
    filename: url,
    encounterId: encounterId as string,
  });
  const [objectUrl, setObjectUrl] = useState<string | undefined>();
  const { width, ref } = useResizeDetector({
    refreshMode: "debounce",
    refreshRate: 100,
  });

  const isPdf = url.toLowerCase().endsWith(".pdf");

  useEffect(() => {
    if (attachmentBlob) {
      const blob = isPdf
        ? new Blob([attachmentBlob], { type: "application/pdf" })
        : attachmentBlob;
      const objUrl = URL.createObjectURL(blob);
      setObjectUrl(objUrl);
      return () => URL.revokeObjectURL(objUrl);
    }
  }, [attachmentBlob, isPdf]);

  if (!objectUrl) {
    return <Skeleton className="aspect-square w-full" />;
  }

  if (isPdf) {
    return (
      <a ref={ref} href={objectUrl} target="_blank" rel="noreferrer">
        <Tooltip tooltipContentText="Open PDF in new tab" side="bottom">
          <Document
            file={objectUrl}
            className="aspect-square bg-tertiary rounded-lg flex items-center justify-center overflow-hidden"
            loading={<Skeleton className="h-40 w-full" />}
          >
            <Page
              className="mt-4 shadow-lg"
              width={width && width > 0 ? width - 16 : undefined}
              pageIndex={0}
              renderTextLayer={false}
              renderAnnotationLayer={false}
            />
          </Document>
        </Tooltip>
      </a>
    );
  }

  return (
    <MagnifyImage
      className={cn(
        "bg-secondary rounded-lg object-contain aspect-square",
        className,
      )}
      src={objectUrl}
      magnifyImageOffsetRef={magnifyImageOffsetRef}
    />
  );
};

const ALLERGY_COPY = {
  nkdaSuccess: {
    title: "Allergies Auto-charted",
    description:
      "The patient's allergy list was auto-charted as No Known Drug Allergies. Review and confirm the allergy list.",
  },
  greatSuccess: {
    title: "Allergies Auto-charted",
    description:
      "All allergies reported in the CIQ were auto-charted. Review and confirm the allergy list.",
  },
  needsReconciliation: {
    title: "Allergies Need Reconciliation",
    description:
      "Need manual entry. Review and update the allergy list. Patient allergies were unable to be auto-charted.",
  },
} as const;

export const StructuredAllergies = ({
  allergies,
}: {
  allergies: VisitIntakeAllergyData;
}) => {
  const {
    processingSuccessful,
    nkaIndicated,
    manualReconciliationRequired,
    freetextAllergies,
    codifiedAllergies,
  } = allergies;

  // Indicates auto-charting was entirely skipped. Providers do not want a callout in this case.
  if (manualReconciliationRequired) {
    return null;
  }

  const hasFreetextAllergies = freetextAllergies.length > 0;

  const allergiesSuccess =
    processingSuccessful &&
    !nkaIndicated &&
    !hasFreetextAllergies &&
    codifiedAllergies.length > 0;

  const nkaSuccess = nkaIndicated && processingSuccessful;

  let copy: { title: string; description: string };
  let variant: "warning" | "info" = "warning";

  if (nkaSuccess) {
    copy = ALLERGY_COPY.nkdaSuccess;
    variant = "info";
  } else if (allergiesSuccess) {
    copy = ALLERGY_COPY.greatSuccess;
    variant = "info";
  } else {
    copy = ALLERGY_COPY.needsReconciliation;
    variant = "warning";
  }

  return (
    <li className="list-none -ml-5 mt-2 space-y-2">
      <Callout
        variant={variant}
        subtle
        title={copy.title}
        description={copy.description}
      />
    </li>
  );
};

export const AmazonCiq = () => {
  const magnifyImageOffsetRef = useRef<HTMLDivElement>(null);
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data: encounter } = useEncounter(
    encounterId as string,
    patientId as string,
  );
  const { data: isAmazonMedicationExtractionEnabled } = useConfigCatFeatureFlag(
    "amazon-questionnaire-response-medication-extraction",
  );
  const { data: isAmazonConditionExtractionEnabled } = useConfigCatFeatureFlag(
    "amazon-questionnaire-response-condition-extraction",
  );
  const { data: isAmazonAllergyExtractionEnabled } = useConfigCatFeatureFlag(
    "amazon-questionnaire-response-allergy-extraction",
  );

  const { data: roomToken } = useRoomToken({
    encounterId: encounterId as string,
    patientId: patientId as string,
  });

  const {
    data: amazonVisitIntake,
    isLoading: isAmazonVisitIntakeLoading,
    isError: isAmazonVisitIntakeError,
    refetch: refetchAmazonVisitIntake,
  } = useAmazonVisitIntake({
    patientId: patientId as string,
    amazonVisitId: roomToken as string,
    enabled: Boolean(encounter?.amazonId),
  });

  const sessionAddedMedicationIdsRef = useRef<Set<string>>(new Set());
  const { mutateAsync: recordMedicationExtractionFeedback } =
    useMedicationExtractionFeedback({
      patientId: patientId as string,
      encounterId: String(encounterId ?? ""),
    });
  const { mutate: markAsErroneous, isPending: isMarkingAsErroneous } =
    useMarkMedicationExtractionAsErroneous({
      patientId: patientId as string,
      amazonVisitId: roomToken as string,
    });

  const activeMedications =
    amazonVisitIntake?.medications?.activeMedications ?? [];
  const btSpanId = amazonVisitIntake?.medications?.btSpanId;
  const initiallyAddedMedicationIds = useMemo(
    () =>
      new Set(
        activeMedications
          .filter((m) => m.codifiedDrugs?.some((d) => d.addedToChartAt))
          .map((m) => `${m.sourceId}-${m.name}`),
      ),
    [activeMedications],
  );
  const totalMedications = activeMedications.length;
  const erroneousSuggestions = Boolean(
    amazonVisitIntake?.medications?.markedAsErroneousAt,
  );

  const recordMedicationFeedback = useCallback(
    async (erroneousSuggestionsOverride?: boolean) => {
      if (!btSpanId) return;
      const allAddedMedicationIds = new Set([
        ...initiallyAddedMedicationIds,
        ...sessionAddedMedicationIdsRef.current,
      ]);
      const totalAdded = allAddedMedicationIds.size;
      const suggestionRelevancy =
        totalMedications > 0 ? totalAdded / totalMedications : 0;
      try {
        await recordMedicationExtractionFeedback({
          btSpanId,
          suggestionRelevancy,
          erroneousSuggestions:
            erroneousSuggestionsOverride ?? erroneousSuggestions,
        });
      } catch {}
    },
    [
      btSpanId,
      initiallyAddedMedicationIds,
      totalMedications,
      erroneousSuggestions,
      recordMedicationExtractionFeedback,
    ],
  );

  const handleMedicationAddSuccess = useCallback(
    async (medication: ProcessedMedication) => {
      const id = `${medication.sourceId}-${medication.name}`;
      sessionAddedMedicationIdsRef.current = new Set(
        sessionAddedMedicationIdsRef.current,
      ).add(id);
      if (btSpanId && encounterId) {
        await recordMedicationFeedback();
      }
    },
    [btSpanId, encounterId, recordMedicationFeedback],
  );

  const sessionAddedConditionIdsRef = useRef<Set<string>>(new Set());
  const { mutateAsync: recordConditionExtractionFeedback } =
    useConditionExtractionFeedback({
      patientId: patientId as string,
      encounterId: String(encounterId ?? ""),
    });
  const {
    mutate: markConditionAsErroneous,
    isPending: isMarkingConditionAsErroneous,
  } = useMarkConditionExtractionAsErroneous({
    patientId: patientId as string,
    amazonVisitId: roomToken as string,
  });

  const historicalConditions =
    amazonVisitIntake?.conditions?.historicalConditions ?? [];
  const conditionBtSpanId = amazonVisitIntake?.conditions?.btSpanId;
  const initiallyAddedConditionIds = useMemo(
    () =>
      new Set(
        historicalConditions
          .filter((c) => c.snomedCodes?.some((s) => s.addedToChartAt))
          .map((c) => `${c.sourceId}-${c.term}`),
      ),
    [historicalConditions],
  );
  const totalConditions = historicalConditions.length;
  const erroneousConditionSuggestions = Boolean(
    amazonVisitIntake?.conditions?.markedAsErroneousAt,
  );

  const recordConditionFeedback = useCallback(
    async (erroneousSuggestionsOverride?: boolean) => {
      if (!conditionBtSpanId) return;
      const allAddedConditionIds = new Set([
        ...initiallyAddedConditionIds,
        ...sessionAddedConditionIdsRef.current,
      ]);
      const totalAdded = allAddedConditionIds.size;
      const suggestionRelevancy =
        totalConditions > 0 ? totalAdded / totalConditions : 0;
      try {
        await recordConditionExtractionFeedback({
          btSpanId: conditionBtSpanId,
          suggestionRelevancy,
          erroneousSuggestions:
            erroneousSuggestionsOverride ?? erroneousConditionSuggestions,
        });
      } catch {}
    },
    [
      conditionBtSpanId,
      initiallyAddedConditionIds,
      totalConditions,
      erroneousConditionSuggestions,
      recordConditionExtractionFeedback,
    ],
  );

  const handleConditionAddSuccess = useCallback(
    async (condition: ProcessedCondition) => {
      const id = `${condition.sourceId}-${condition.term}`;
      sessionAddedConditionIdsRef.current = new Set(
        sessionAddedConditionIdsRef.current,
      ).add(id);
      if (conditionBtSpanId && encounterId) {
        await recordConditionFeedback();
      }
    },
    [conditionBtSpanId, encounterId, recordConditionFeedback],
  );

  if (!encounter || !encounter?.amazonId) {
    return null;
  }

  if (isAmazonVisitIntakeLoading) {
    return <Skeleton className="w-full h-32" />;
  }

  if (isAmazonVisitIntakeError || !amazonVisitIntake) {
    return (
      <Callout variant="danger" title="Failed to load CIQ data">
        <div className="flex justify-around items-center gap-2">
          <Button
            onClick={() => refetchAmazonVisitIntake()}
            variant="secondary"
            size="sm"
          >
            Retry
          </Button>
        </div>
      </Callout>
    );
  }

  const questionnaireResponse =
    amazonVisitIntake?.intakeQuestionnaire?.questionnaireResponse;
  const formattedDate = formatDateTime(questionnaireResponse?.authored);

  // TODO: Move this into JS Fhir Tools
  const eocName =
    (questionnaireResponse?.contained?.[0] as { title?: string } | undefined)
      ?.title || "Amazon Intake";
  const intakeQuestions = (questionnaireResponse?.item ?? []) as CiqItem[];
  const questionnaireResponseId =
    amazonVisitIntake?.intakeQuestionnaire?.questionnaireResponse?.id;

  const getMedicationsForQuestion = (linkId: string): ProcessedMedication[] =>
    activeMedications
      .filter((med) => med.sourceId === linkId)
      .map((med) => ({
        ...med,
        codifiedDrugs: [...(med.codifiedDrugs ?? [])].sort(
          (a, b) => a.drugName.length - b.drugName.length,
        ),
      }));

  const getConditionsForQuestion = (linkId: string): ProcessedCondition[] =>
    historicalConditions.filter((c) => c.sourceId === linkId);

  const currentExtractedAllergies =
    amazonVisitIntake?.allergyExtraction?.currentAllergies ?? [];
  const allergyMismatches =
    amazonVisitIntake?.allergyExtraction?.amazonRxcuisApothraMismatch ?? [];
  const isNkaMarked = Boolean(
    amazonVisitIntake?.allergyExtraction?.nkaMarkedAt,
  );

  const getAllergiesForQuestion = (linkId: string): ExtractedAllergy[] =>
    currentExtractedAllergies.filter((a) => a.sourceId === linkId);

  return (
    <Collapsible
      title={`Intake Questions - ${formattedDate} - ${eocName}`}
      headerSize="sm"
      defaultOpen={true}
    >
      <div ref={magnifyImageOffsetRef} className="flex flex-col gap-1 mt-2">
        {intakeQuestions.map((question: CiqItem, questionIndex: number) => {
          const { linkId, text, answer } = question;
          if (!answer?.length) return null;

          const questionMedications = getMedicationsForQuestion(linkId);
          const questionConditions = getConditionsForQuestion(linkId);
          const questionAllergies = getAllergiesForQuestion(linkId);

          const questionAllergyMismatches =
            linkId === STATIC_ALLERGEN_CIQ_LINK_ID ? allergyMismatches : [];
          const showMedications =
            isAmazonMedicationExtractionEnabled &&
            questionMedications.length > 0;
          const showConditions =
            isAmazonConditionExtractionEnabled && questionConditions.length > 0;
          const showAllergies =
            isAmazonAllergyExtractionEnabled && questionAllergies.length > 0;
          const showAllergyMismatches =
            isAmazonAllergyExtractionEnabled &&
            questionAllergyMismatches.length > 0;
          const showNoKnownAllergies =
            isAmazonAllergyExtractionEnabled &&
            linkId === STATIC_ALLERGEN_CIQ_LINK_ID &&
            isNkaMarked;
          const hasExtractionContent =
            showMedications ||
            showConditions ||
            showAllergies ||
            showAllergyMismatches ||
            showNoKnownAllergies;

          const isLastQuestion =
            questionIndex ===
            intakeQuestions.filter((q: CiqItem) => q.answer?.length).length - 1;

          const allAttachments = answer.every(
            (ans) => "valueAttachment" in ans && ans.valueAttachment?.url,
          );

          return (
            <div key={linkId}>
              <BodyText size="sm" bold className="mb-0.5">
                {text}
              </BodyText>

              {allAttachments ? (
                <div className="mt-2 grid lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {answer.map((ans) => {
                    const url = ans.valueAttachment?.url;
                    if (!url) return null;
                    const fileId = url.split("/").pop() || "file";
                    return (
                      <InlineAttachment
                        key={`${linkId}-${fileId}`}
                        url={url}
                        className="aspect-square"
                        magnifyImageOffsetRef={magnifyImageOffsetRef}
                      />
                    );
                  })}
                </div>
              ) : (
                <>
                  <ul className="pl-5 list-disc space-y-1">
                    {answer.map((ans) => {
                      if ("valueString" in ans && ans.valueString) {
                        const isSkipped =
                          ans.valueString === "skipped_question";
                        const valueToParse = isSkipped
                          ? "Patient Skipped Question"
                          : ans.valueString;
                        const answerText = parse(valueToParse);
                        return (
                          <li
                            key={`${linkId}-${valueToParse.replace(/\s+/g, "-").substring(0, 20)}`}
                          >
                            <BodyText
                              size="sm"
                              className={
                                isSkipped ? "text-secondary italic" : undefined
                              }
                            >
                              {answerText}
                            </BodyText>
                          </li>
                        );
                      }

                      if (
                        "valueAttachment" in ans &&
                        ans.valueAttachment?.url
                      ) {
                        const url = ans.valueAttachment.url;
                        const fileId = url.split("/").pop() || "file";
                        return (
                          <li
                            key={`${linkId}-${fileId}`}
                            className="list-none -ml-5"
                          >
                            <InlineAttachment url={url} />
                          </li>
                        );
                      }

                      return null;
                    })}
                    {/* {linkId === STATIC_ALLERGEN_CIQ_LINK_ID &&
                      amazonVisitIntake?.allergies && (
                        <StructuredAllergies
                          allergies={amazonVisitIntake.allergies}
                        />
                      )} */}
                  </ul>
                  {hasExtractionContent && (
                    <div className="flex flex-col gap-2 mt-1.5">
                      {showAllergyMismatches &&
                        questionAllergyMismatches.map((mismatch) => (
                          <CiqAllergyMismatch
                            key={`${linkId}-mismatch-${mismatch.allergenId}`}
                            mismatch={mismatch}
                            patientId={patientId as string}
                            encounterId={encounterId as string | undefined}
                            questionnaireResponseId={questionnaireResponseId}
                          />
                        ))}
                      {showNoKnownAllergies && <CiqAllergyNoKnown />}
                      {showAllergies &&
                        questionAllergies.map((allergy) => (
                          <CiqAllergy
                            key={`${linkId}-${allergy.name ?? allergy.sourceId}`}
                            allergy={allergy}
                            patientId={patientId as string}
                            encounterId={encounterId as string | undefined}
                            questionnaireResponseId={questionnaireResponseId}
                          />
                        ))}
                      {showMedications &&
                        questionMedications.map((medication) => (
                          <CiqMedication
                            key={`${linkId}-${medication.name}`}
                            medication={medication}
                            patientId={patientId as string}
                            encounterId={encounterId as string | undefined}
                            questionnaireResponseId={questionnaireResponseId}
                            onMedicationAddSuccess={
                              btSpanId ? handleMedicationAddSuccess : undefined
                            }
                          />
                        ))}
                      {showConditions &&
                        questionConditions.map((condition) => (
                          <CiqCondition
                            key={`${linkId}-${condition.term}`}
                            condition={condition}
                            patientId={patientId as string}
                            amazonVisitId={roomToken as string}
                            questionnaireResponseId={questionnaireResponseId}
                            onConditionAddSuccess={
                              conditionBtSpanId
                                ? handleConditionAddSuccess
                                : undefined
                            }
                          />
                        ))}
                      {questionnaireResponseId && showConditions && (
                        <Tooltip
                          tooltipContentText="Incorrect Suggestions"
                          triggerClassName="self-start mt-1"
                        >
                          <Button
                            type="button"
                            variant="icon"
                            size="xs"
                            className="!p-0.5"
                            onClick={() =>
                              markConditionAsErroneous(
                                { questionnaireResponseId },
                                {
                                  onSuccess: () =>
                                    recordConditionFeedback(true),
                                },
                              )
                            }
                            disabled={
                              erroneousConditionSuggestions ||
                              isMarkingConditionAsErroneous
                            }
                            aria-label="Incorrect Suggestions"
                          >
                            <ThumbsDown
                              className={cn(
                                "w-4 h-4",
                                erroneousConditionSuggestions ||
                                  isMarkingConditionAsErroneous
                                  ? erroneousConditionSuggestions
                                    ? "stroke-[#8E3CB580] fill-[#8E3CB533]"
                                    : "stroke-[#8E3CB580]"
                                  : "stroke-ai",
                              )}
                            />
                          </Button>
                        </Tooltip>
                      )}
                      {questionnaireResponseId && showMedications && (
                        <Tooltip
                          tooltipContentText="Incorrect Suggestions"
                          triggerClassName="self-start mt-1"
                        >
                          <Button
                            type="button"
                            variant="icon"
                            size="xs"
                            className="!p-0.5"
                            onClick={() =>
                              markAsErroneous(
                                { questionnaireResponseId },
                                {
                                  onSuccess: () =>
                                    recordMedicationFeedback(true),
                                },
                              )
                            }
                            disabled={
                              erroneousSuggestions || isMarkingAsErroneous
                            }
                            aria-label="Incorrect Suggestions"
                          >
                            <ThumbsDown
                              className={cn(
                                "w-4 h-4",
                                erroneousSuggestions || isMarkingAsErroneous
                                  ? erroneousSuggestions
                                    ? "stroke-[#8E3CB580] fill-[#8E3CB533]"
                                    : "stroke-[#8E3CB580]"
                                  : "stroke-ai",
                              )}
                            />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  )}
                </>
              )}

              {!isLastQuestion && <Separator className="mt-1 mb-1" />}
            </div>
          );
        })}
      </div>
    </Collapsible>
  );
};
